import { Component } from '@theme/component';
import { ThemeEvents, WishlistUpdateEvent } from '@theme/events';

const STORAGE_KEY = 'omerella-wishlist';

/**
 * @typedef {object} WishlistItem
 * @property {string} id
 * @property {string} [handle]
 * @property {string} [title]
 * @property {string} [url]
 * @property {string} [image]
 * @property {string} [price]
 */

/** @returns {WishlistItem[]} */
export function getWishlistItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    if (!Array.isArray(parsed)) return [];

    return parsed.map((item) => {
      if (typeof item === 'string' || typeof item === 'number') {
        return { id: String(item) };
      }
      if (item && typeof item === 'object' && item.id != null) {
        return { ...item, id: String(item.id) };
      }
      return null;
    }).filter(Boolean);
  } catch {
    return [];
  }
}

/** @returns {string[]} */
export function getWishlistIds() {
  return getWishlistItems().map((item) => item.id);
}

/** @param {WishlistItem[]} items */
function saveWishlistItems(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  sessionStorage.setItem(
    'wishlist-count',
    JSON.stringify({
      value: String(items.length),
      timestamp: Date.now(),
    })
  );
}

/** @param {string} productId */
export function isInWishlist(productId) {
  return getWishlistIds().includes(String(productId));
}

/**
 * @param {HTMLButtonElement} button
 * @returns {WishlistItem}
 */
function getItemFromButton(button) {
  return {
    id: String(button.dataset.productId || ''),
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
  };
}

/**
 * @param {string} productId
 * @param {string} [sourceId]
 * @param {WishlistItem} [meta]
 * @returns {boolean}
 */
export function toggleWishlistItem(productId, sourceId = '', meta) {
  const id = String(productId);
  const items = getWishlistItems();
  const index = items.findIndex((item) => item.id === id);
  let added = false;

  if (index === -1) {
    items.push(meta || { id });
    added = true;
  } else {
    items.splice(index, 1);
  }

  saveWishlistItems(items);
  document.dispatchEvent(new WishlistUpdateEvent(getWishlistIds(), sourceId, { productId: id, added }));
  syncWishlistButtons(id);
  renderWishlistPages();
  return added;
}

/** @param {string} [productId] */
export function syncWishlistButtons(productId) {
  const buttons = productId
    ? document.querySelectorAll(`.omerella-wishlist-btn[data-product-id="${productId}"]`)
    : document.querySelectorAll('.omerella-wishlist-btn');

  buttons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const pid = button.dataset.productId;
    if (!pid) return;
    const active = isInWishlist(pid);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      active ? button.dataset.removeLabel || 'Remove from wishlist' : button.dataset.addLabel || 'Add to wishlist'
    );
  });
}

export function renderWishlistPages() {
  document.querySelectorAll('omerella-wishlist-page').forEach((page) => {
    if (page instanceof OmerellaWishlistPage) {
      page.render();
    }
  });
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistCount
 */

/** @extends {Component<Refs>} */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistCount'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('click', this.onWishlistButtonClick);
    this.renderCount(getWishlistIds().length);
    syncWishlistButtons();
    renderWishlistPages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('click', this.onWishlistButtonClick);
  }

  /** @param {PageTransitionEvent} event */
  onPageShow = (event) => {
    if (event.persisted) {
      this.renderCount(getWishlistIds().length);
      syncWishlistButtons();
      renderWishlistPages();
    }
  };

  /** @param {WishlistUpdateEvent} event */
  onWishlistUpdate = (event) => {
    const count = event.detail.data?.itemCount ?? getWishlistIds().length;
    this.renderCount(count);
  };

  /** @param {MouseEvent} event */
  onWishlistButtonClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.omerella-wishlist-btn');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const productId = button.dataset.productId;
    if (!productId) return;

    toggleWishlistItem(productId, button.id || 'omerella-wishlist-btn', getItemFromButton(button));
  };

  /** @param {number} count */
  renderCount(count) {
    if (!this.refs.wishlistCount) return;
    this.refs.wishlistCount.textContent = count > 0 ? (count < 100 ? String(count) : '99+') : '0';
    this.classList.toggle('header-actions__wishlist--has-items', count > 0);
  }
}

class OmerellaWishlistPage extends HTMLElement {
  connectedCallback() {
    this.#grid = this.querySelector('[data-wishlist-grid]');
    this.#empty = this.querySelector('[data-wishlist-empty]');
    document.addEventListener(ThemeEvents.wishlistUpdate, this.render);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.render);
  }

  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLElement | null} */
  #empty = null;

  render = () => {
    if (!this.#grid || !this.#empty) return;

    const items = getWishlistItems();
    this.#empty.hidden = items.length > 0;
    this.#grid.hidden = items.length === 0;

    if (!items.length) {
      this.#grid.innerHTML = '';
      return;
    }

    this.#grid.innerHTML = items
      .map((item) => {
        const title = item.title || 'Product';
        const url = item.url || '#';
        const image = item.image
          ? `<img src="${item.image}" alt="${title}" class="omerella-wishlist-page__image" loading="lazy" width="400" height="500">`
          : `<div class="omerella-wishlist-page__image omerella-wishlist-page__image--placeholder"></div>`;
        const price = item.price ? `<p class="omerella-wishlist-page__price">${item.price}</p>` : '';

        return `
          <article class="omerella-wishlist-page__card omerella-product-card omerella-product-card--wishlist">
            <div class="omerella-product-card__media">
              <a href="${url}" class="omerella-wishlist-page__media-link">${image}</a>
            </div>
            <div class="omerella-product-card__info">
              <p class="omerella-product-card__title"><a href="${url}">${title}</a></p>
              ${price}
            </div>
            <button
              type="button"
              class="omerella-wishlist-btn is-active"
              data-product-id="${item.id}"
              data-product-handle="${item.handle || ''}"
              data-product-title="${title}"
              data-product-url="${url}"
              data-product-image="${item.image || ''}"
              data-product-price="${item.price || ''}"
              data-add-label="Add to wishlist"
              data-remove-label="Remove from wishlist"
              aria-pressed="true"
              aria-label="Remove from wishlist"
            >
              <svg class="omerella-wishlist-btn__icon omerella-wishlist-btn__icon--outline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </button>
          </article>
        `;
      })
      .join('');
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

if (!customElements.get('omerella-wishlist-page')) {
  customElements.define('omerella-wishlist-page', OmerellaWishlistPage);
}
